import { useState, useEffect } from 'react'
import styled, { CSSObject } from '@emotion/styled'
import { useLocation, useMatch, useNavigate } from 'react-router-dom'
import { useConsumerCart, useModal, useShop, useCartMatch } from 'shop/hooks'
import {
  NavBar,
  Banner,
  PromoBanner,
  Metadata,
  StickyCartTotalMobileFooter
} from 'shop/components'
import { StyledHTMLElement } from 'shop/theme/types'
import {
  ShopBody,
  ShopFlashMessages,
  ShopDetails,
  ShopInformation,
  ShopBreadcrumbs
} from 'shop/components/Shop'
import { MetaDescription, PageTitle, Canonical } from 'shop/components/SEO'
import ProductErrorModal from 'shop/components/ProductInfo/ProductErrorModal'
import {
  SetupShopPageStore,
  ShopPageContext
} from 'shop/hooks/usePageContext/useShopPage'
import CartErrorModal from 'shop/components/ErrorModals/CartErrorModal'
import { PAGE_SHOP } from 'shop/components/NavBar/utils'

interface Props {
  slug: string
}

const Shop = ({ slug }: Props) => {
  const navigate = useNavigate()
  const { pathname, state } = useLocation()
  const { cartError, cart, cartLoading } = useConsumerCart()
  const { isCartMatch } = useCartMatch()
  const { currentPartnerStore } = useShop()
  const { openModal, isModalOpen, portalModal } = useModal()

  const [hasProductModalError, setHasProductModalError] = useState(false)

  const [modalMode, setModalMode] = useState(false)

  const match = useMatch('/store/:store/:product')

  const { seo: storeSeo } = currentPartnerStore || {}

  useEffect(() => {
    if (state?.hasError) {
      setHasProductModalError(true)
    }
  }, [state?.hasError])

  useEffect(() => {
    if (match) {
      setModalMode(true)
    } else {
      setModalMode(false)
    }
  }, [match])

  const handleOpenCartModal = () => {
    if (cartLoading) return
    openModal('cart')
  }

  return (
    <ShopPageContext.Provider value={SetupShopPageStore()}>
      {!modalMode && <Metadata storeSlug={slug} />}
      {!!cartError && <CartErrorModal errorCode={cartError || ''} />}
      {isModalOpen('cart') && portalModal('cart')}
      {isModalOpen('upsell') && portalModal('upsell')}
      <Canonical url={window.location.origin + window.location.pathname} />
      <PageTitle />
      <MetaDescription seoDescription={storeSeo?.description} type='store' />
      <Background>
        <ShopFlashMessages />
        <ProductErrorModal
          hasError={hasProductModalError}
          onClose={() => {
            setHasProductModalError(false)
            // we must remove the state to prevent it appearing after refresh
            navigate(pathname, {
              replace: true,
              state: { hasError: false }
            })
          }}
        />
        <PromoBanner />
        <NavBar transparent={false} page={PAGE_SHOP} storeSlug={slug} />
        <Banner />
        <ShopContent>
          <ShopDetails />
          <ShopBody slug={slug} />
          <ShopInformation />
          <ShopBreadcrumbs />
        </ShopContent>

        {isCartMatch && !!cart?.orderItems?.length && (
          <StickyCartTotalMobileFooter onClick={handleOpenCartModal} />
        )}
        <Bottom />
      </Background>
    </ShopPageContext.Provider>
  )
}

const Background = styled.div(
  (): CSSObject => ({
    backgroundColor: '#f5f5f5',
    position: 'relative', // iOS sticky NavBar fix
    display: 'block' // iOS sticky NavBar fix
  })
)

const Bottom = styled.div(() => ({
  height: '80px'
}))

const ShopContent = styled.div<StyledHTMLElement>(({ theme }) => ({
  maxWidth: '1332px',
  margin: '0 auto',
  [theme.mediaQueries.viewport7]: {
    padding: '0 16px 16px 16px'
  },
  [theme.mediaQueries.viewport14]: {
    maxWidth: '2064px',
    padding: '0 72px 24px 72px'
  }
}))

export default Shop
